import { formatUserFacingError, withErrorCode } from '@/lib/errors'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { serverTimestamp } from 'firebase/firestore'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { SearchMultiSelect } from '@/components/common/SearchMultiSelect'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { MemberPhotoUploader, type MemberPhotoPatch } from '@/components/common/MemberPhotoUploader'
import { adminCompanyService } from '../services/admin.company.service'
import { useAdminPositions } from '../hooks/useAdminPositions'
import { typedDoc, updateDocument } from '@/lib/firebase/firestore'
import type { CompanyMemberDocument } from '@/types'

const memberSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  bio: z.string().min(1, 'Bio is required'),
  // roles is denormalized from positionIds at save time; not validated here.
  roles: z.array(z.string()),
  positionIds: z.array(z.string()).min(1, 'At least one position is required'),
  isActive: z.boolean(),
  isPublished: z.boolean(),
  availableForHire: z.boolean(),
  seasonNumbers: z.array(z.number().int().min(1)),
  order: z.number().int().min(0),
  socialLinks: z.object({
    instagram: z.string(),
    website: z.string(),
  }),
  email: z.string().email('Must be a valid email').or(z.literal('')),
  photoCredit: z.string().optional(),
})

function toSlug(str: string): string {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

const defaultFormValues = {
  name: '',
  bio: '',
  roles: [] as string[],
  positionIds: [] as string[],
  isActive: true,
  isPublished: false,
  availableForHire: false,
  seasonNumbers: [] as number[],
  order: 0,
  socialLinks: {
    instagram: '',
    website: '',
  },
  email: '',
  photoCredit: '',
}

interface AdminMemberEditModalProps {
  open: boolean
  memberId: string | null
  onClose: () => void
  onSaved?: () => void
}

export function AdminMemberEditModal({ open, memberId, onClose, onSaved }: AdminMemberEditModalProps) {
  const isNew = memberId === null

  const { positions, loading: positionsLoading } = useAdminPositions()
  const activePositions = positions.filter((p) => p.isActive)

  const [loading, setLoading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const [squarePhotoUrl, setSquarePhotoUrl] = useState('')
  const [portraitPhotoUrl, setPortraitPhotoUrl] = useState('')

  const [currentSlug, setCurrentSlug] = useState('')
  const [seasonNumbersInput, setSeasonNumbersInput] = useState('')

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof memberSchema>>({
    resolver: zodResolver(memberSchema),
    defaultValues: defaultFormValues,
  })

  const watchedName = watch('name')
  const watchedPositionIds = watch('positionIds')
  const watchedIsActive = watch('isActive')
  const watchedIsPublished = watch('isPublished')
  const watchedAvailableForHire = watch('availableForHire')

  useEffect(() => {
    setCurrentSlug(toSlug(watchedName))
  }, [watchedName])

  // Reset state and load member data when modal opens
  useEffect(() => {
    if (!open) return

    setSubmitError(null)
    setSquarePhotoUrl('')
    setPortraitPhotoUrl('')
    setSeasonNumbersInput('')

    if (isNew) {
      reset(defaultFormValues)
      setCurrentSlug('')
      return
    }

    setLoading(true)
    adminCompanyService
      .getAllMembers()
      .then((all) => {
        const found = all.find((m) => m.id === memberId)
        if (found) {
          reset({
            name: found.name,
            bio: found.bio,
            roles: found.roles,
            positionIds: found.positionIds ?? [],
            isActive: found.isActive,
            isPublished: found.isPublished,
            availableForHire: found.availableForHire ?? false,
            seasonNumbers: found.seasonNumbers,
            order: found.order,
            socialLinks: {
              instagram: found.socialLinks?.instagram ?? '',
              website: found.socialLinks?.website ?? '',
            },
            email: found.email ?? '',
          })
          setSquarePhotoUrl(found.squarePhotoUrl ?? found.photoUrl ?? '')
          setPortraitPhotoUrl(found.portraitPhotoUrl ?? '')
          setCurrentSlug(found.slug)
          setSeasonNumbersInput(found.seasonNumbers.join(', '))
          if (found.photoCredit) setValue('photoCredit', found.photoCredit)
        }
      })
      .catch((err: unknown) =>
        setSubmitError(formatUserFacingError(err, 'Failed to load member'))
      )
      .finally(() => setLoading(false))
  }, [open, memberId, isNew, reset, setValue])

  function handleSeasonNumbersChange(value: string) {
    setSeasonNumbersInput(value)
    const nums = value
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s !== '')
      .map(Number)
      .filter((n) => !isNaN(n) && n > 0)
    setValue('seasonNumbers', nums)
  }

  async function handlePhotosUploaded(patch: MemberPhotoPatch) {
    if (patch.squarePhotoUrl) setSquarePhotoUrl(patch.squarePhotoUrl)
    if (patch.portraitPhotoUrl) setPortraitPhotoUrl(patch.portraitPhotoUrl)
    if (!isNew && memberId) {
      const docRef = typedDoc<CompanyMemberDocument>('company', memberId)
      await updateDocument(docRef, {
        ...patch,
        updatedAt: serverTimestamp(),
      })
    }
  }

  async function onSubmit(values: z.infer<typeof memberSchema>) {
    setSubmitError(null)

    if (values.isPublished && (!squarePhotoUrl || !portraitPhotoUrl)) {
      setSubmitError(withErrorCode('Both the square and portrait photos are required before publishing.', 'app/validation'))
      return
    }

    // Derive titles (denormalized for display) from canonical positionIds.
    const titlesById = new Map(activePositions.map((p) => [p.id, p.title]))
    const derivedRoles = values.positionIds
      .map((pid) => titlesById.get(pid))
      .filter((t): t is string => !!t)
    const enrichedValues = { ...values, roles: derivedRoles }

    try {
      if (isNew) {
        const newId = await adminCompanyService.createMember(enrichedValues)
        if (squarePhotoUrl || portraitPhotoUrl) {
          const docRef = typedDoc<CompanyMemberDocument>('company', newId)
          await updateDocument(docRef, {
            ...(squarePhotoUrl && { squarePhotoUrl }),
            ...(portraitPhotoUrl && { portraitPhotoUrl }),
            updatedAt: serverTimestamp(),
          })
        }
      } else if (memberId) {
        await adminCompanyService.updateMember(memberId, enrichedValues)
      }
      onSaved?.()
      onClose()
    } catch (err) {
      setSubmitError(formatUserFacingError(err, 'Failed to save member'))
    }
  }

  const bothPhotosUploaded = !!squarePhotoUrl && !!portraitPhotoUrl

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isNew ? 'Add a company member' : 'Edit Member'}</DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="space-y-4 py-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : (
            <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-5">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">

                {/* Left column — primary info */}
                <div className="space-y-5">
                  <div className="space-y-1">
                    <Label htmlFor="name">Name</Label>
                    <Input id="name" type="text" placeholder="Jane Doe" {...register('name')} />
                    {watchedName && (
                      <p className="text-xs text-muted-foreground">Slug: {currentSlug}</p>
                    )}
                    {errors.name && (
                      <p className="text-sm text-destructive">{withErrorCode(String(errors.name.message), 'app/validation')}</p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" placeholder="jane@example.com" {...register('email')} />
                    <p className="text-xs text-muted-foreground">
                      Used to auto-detect when this person creates an account.
                    </p>
                    {errors.email && (
                      <p className="text-sm text-destructive">{withErrorCode(String(errors.email.message), 'app/validation')}</p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="bio">Bio</Label>
                    <Textarea
                      id="bio"
                      rows={5}
                      placeholder="Member bio…"
                      {...register('bio')}
                    />
                    {errors.bio && (
                      <p className="text-sm text-destructive">{withErrorCode(String(errors.bio.message), 'app/validation')}</p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="order">Sort Order</Label>
                    <Input id="order" type="number" min={0} {...register('order', { valueAsNumber: true })} />
                    {errors.order && (
                      <p className="text-sm text-destructive">{withErrorCode(String(errors.order.message), 'app/validation')}</p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="seasonNumbers">Season Numbers</Label>
                    <Input
                      id="seasonNumbers"
                      type="text"
                      placeholder="1, 2, 3"
                      value={seasonNumbersInput}
                      onChange={(e) => handleSeasonNumbersChange(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Comma-separated list of season numbers this member appeared in.
                    </p>
                    {errors.seasonNumbers && (
                      <p className="text-sm text-destructive">{withErrorCode(String(errors.seasonNumbers.message), 'app/validation')}</p>
                    )}
                  </div>

                  {/* Social links */}
                  <div className="space-y-3">
                    <Label className="text-base font-semibold">Social Links</Label>

                    <div className="space-y-1">
                      <Label htmlFor="instagram">Instagram</Label>
                      <Input
                        id="instagram"
                        type="text"
                        placeholder="@username or full URL"
                        {...register('socialLinks.instagram')}
                      />
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="website">Website</Label>
                      <Input
                        id="website"
                        type="text"
                        placeholder="https://example.com"
                        {...register('socialLinks.website')}
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="photoCredit">Photo Credit</Label>
                    <Input
                      id="photoCredit"
                      type="text"
                      placeholder="Photo by Jane Doe"
                      {...register('photoCredit')}
                    />
                    <p className="text-xs text-muted-foreground">Shown as a tooltip when hovering over their photo.</p>
                  </div>
                </div>

                {/* Right column — roles, toggles, photos */}
                <div className="space-y-5">
                  {/* Roles */}
                  {positionsLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-12" />
                      <Skeleton className="h-9 w-full" />
                    </div>
                  ) : activePositions.length === 0 ? (
                    <div className="space-y-1">
                      <Label>Roles</Label>
                      <p className="text-sm text-muted-foreground">
                        No positions configured.{' '}
                        <Link to="/admin/config" className="underline">
                          Add some in Configuration.
                        </Link>
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <SearchMultiSelect
                        label="Roles"
                        items={activePositions.map((p) => ({ id: p.id, label: p.title }))}
                        selected={watchedPositionIds}
                        onChange={(ids) => setValue('positionIds', ids)}
                      />
                      {errors.positionIds && (
                        <p className="text-sm text-destructive">{withErrorCode(String(errors.positionIds.message), 'app/validation')}</p>
                      )}
                    </div>
                  )}

                  {/* Toggles */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Switch
                        id="isActive"
                        checked={watchedIsActive}
                        onCheckedChange={(checked) => setValue('isActive', checked)}
                      />
                      <Label htmlFor="isActive">Active member</Label>
                    </div>

                    <div className="flex items-start gap-3">
                      <Switch
                        id="isPublished"
                        checked={watchedIsPublished}
                        onCheckedChange={(checked) => setValue('isPublished', checked)}
                      />
                      <div className="flex flex-col gap-0.5">
                        <Label htmlFor="isPublished">Published</Label>
                        {watchedIsPublished && !bothPhotosUploaded && (
                          <p className="text-xs text-amber-500">
                            {withErrorCode('Both photos are required to publish.', 'app/validation')}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Switch
                        id="availableForHire"
                        checked={watchedAvailableForHire}
                        onCheckedChange={(checked) => setValue('availableForHire', checked)}
                      />
                      <div className="flex flex-col gap-0.5">
                        <Label htmlFor="availableForHire">Available for Hire</Label>
                        <p className="text-xs text-muted-foreground">Shows in service request provider picker</p>
                      </div>
                    </div>
                  </div>

                  {/* Photo uploads */}
                  <MemberPhotoUploader
                    slug={toSlug(watchedName) || 'member'}
                    squarePhotoUrl={squarePhotoUrl}
                    portraitPhotoUrl={portraitPhotoUrl}
                    onUploaded={handlePhotosUploaded}
                    disabled={!watchedName}
                    disabledReason="Enter a name first."
                    helperText="Both photos are required before the member can be published."
                  />
                </div>
              </div>

              {submitError && <p className="text-sm text-destructive">{submitError}</p>}

              <div className="flex justify-end gap-3 pt-2">
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving…' : isNew ? 'Create Member' : 'Save Changes'}
                </Button>
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
